import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConsultClientService } from 'src/app/services/consult-client.service';
import { STEPPER_LIMITATIONS, SuggestingService } from 'src/app/services/suggesting.service';
import { INVESTMENT_GOAL_ROUTE, NEW_CLIENT_ROUTE, RESET_WITHDRAWAL_DRAFT_ROUTES, WITHDRAWAL_AVAILABLE_CLIENTS_ROUTE, WITHDRAWAL_CHECK_ROUTE, WITHDRAWAL_COMMENT_ROUTE, WITHDRAWAL_FORM_ROUTE } from '../constants';
import { WithdrawalDraftStorageService } from 'src/app/services/withdrawal-draft-storage.service';

@Component({
  selector: 'libero-stepper',
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss',
  standalone: false
})

export class StepperComponent implements OnInit, OnDestroy {
  private STEPS_ALL = 8;
  private STEPS_LIMITED = 5;
  private STEPS_WITHDRAWAL_FORM = 4;
  private STEPS_WITHDRAWAL_FORM_LIMITED = 3;

  private stepsSub?: Subscription;
  private clientDetailSuggestingMode = false;

  private _numOfSteps = this.STEPS_ALL;

  public get numOfSteps(): number {
    const steps = this.consultClientService.getAllSteps();

    if (steps.routeFromClientList) {
      return this.STEPS_WITHDRAWAL_FORM;
    }
    if(steps.draftForm) {
      return this.STEPS_WITHDRAWAL_FORM_LIMITED;
    }
    const newClient = steps.newClient || this.router.url.includes(NEW_CLIENT_ROUTE);

    if(this.route.snapshot.queryParams['steps'] === STEPPER_LIMITATIONS.STEPS_LIMITED || this.route.snapshot.queryParams['suggesting']) {
      return this.STEPS_LIMITED;
    } else {
      return newClient ? this._numOfSteps - 2 : this._numOfSteps;
    }
  }

  public get activeIndex(): number {
    //0 = 1st step
    if(this.isFirstStep) {
      return 0;
    // client detail shows stepper only if part of suggesting process
    } else if (this.isSecondStep) {
      return 1;
    } else if (this.isThirdStep) {
      return this.adjustStepIfLimited(2);
    } else if (this.isFourthStep) {
      return this.adjustStepIfLimited(3);
    } else if (this.isFifthStep) {
      return this.adjustStepIfLimited(4);
    } else if (this.isSixthStep) {
      return this.adjustStepIfLimited(5);
    } else if (this.isSeventhStep) {
      return this.adjustStepIfLimited(6);
    } else if (this.isEighthStep) {
      return this.adjustStepIfLimited(7);
    } else {
      // unknown url; do not display stepper
      return -1;
    }
  }

  private get isFirstStep(): boolean {
    const consultSteps = this.consultClientService.getAllSteps();
    return this.router.url.includes('new-draft') ||
      this.router.url.includes('client-picker') ||
      this.router.url.includes(WITHDRAWAL_AVAILABLE_CLIENTS_ROUTE) ||
      !!((
        this.router.url.includes(WITHDRAWAL_FORM_ROUTE) &&
        !consultSteps.routeFromClientList
      ) && consultSteps.draftForm)
  }

  private get isSecondStep(): boolean {
    const consultSteps = this.consultClientService.getAllSteps();
    return this.router.url.includes('workplace-clients') && this.route.snapshot.queryParams['showStepper'] ||
      this.router.url.includes('new-client') ||
      this.router.url.includes('new-client') ||

      (
        this.router.url.includes(WITHDRAWAL_FORM_ROUTE) &&
        consultSteps.routeFromClientList
      ) ||
      (
        this.router.url.includes(WITHDRAWAL_COMMENT_ROUTE) &&
        consultSteps.withdrawalComment &&
        !consultSteps.routeFromClientList
      );
  }

  private get isThirdStep(): boolean {
    const consultSteps = this.consultClientService.getAllSteps();
    return this.router.url.includes('client-detail') && this.clientDetailSuggestingMode ||
      consultSteps.newClient && this.router.url.includes('investment-type') ||
      (this.router.url.includes(WITHDRAWAL_COMMENT_ROUTE) && consultSteps.withdrawalComment) ||
      !!(this.router.url.includes(WITHDRAWAL_CHECK_ROUTE) && consultSteps.withdrawalFinalCheck && !consultSteps.routeFromClientList);
  }

  private get isFourthStep(): boolean {
    const consultSteps = this.consultClientService.getAllSteps();
    return this.router.url.includes(INVESTMENT_GOAL_ROUTE) ||
      (this.router.url.includes(WITHDRAWAL_CHECK_ROUTE) && consultSteps.withdrawalFinalCheck && consultSteps.routeFromClientList) ||
      !!consultSteps.newClient &&
      (this.router.url.includes('suggest-strategy') || this.router.url.includes('suggest-portfolio') || this.router.url.includes('consult-strategy-pick'));
  }

  private get isFifthStep(): boolean {
    const newClient = !!this.consultClientService.getAllSteps()?.newClient;
    return this.router.url.includes('investment-type') ||
      this.router.url.includes('consult-strategy-pick') ||
      newClient && this.router.url.includes('consult-comment')
  }

  private get isSixthStep(): boolean {
    const newClient = !!this.consultClientService.getAllSteps()?.newClient;
    return !newClient && this.router.url.includes('suggest-portfolio') ||
      !newClient && this.router.url.includes('suggest-strategy') ||
      newClient && this.router.url.includes('consult-suggest-check')
  }

  private get isSeventhStep(): boolean {
    return this.router.url.includes('consult-comment');
  }

  private get isEighthStep(): boolean {
    return this.router.url.includes('consult-suggest-check');
  }

  public constructor(
    private router: Router,
    private route: ActivatedRoute,
    private suggestingService: SuggestingService,
    private consultClientService: ConsultClientService,
    private withdrawalDraftStorageService: WithdrawalDraftStorageService,
  ) {}

  private determineClientDetailMode() {
    if (this.router.url.includes('client-detail')) {
      // to prevent accessing it in child components
      const parsedUrl = this.router.parseUrl(this.router.url);
      this.clientDetailSuggestingMode = parsedUrl
        .queryParams['suggesting'] === '1';
    }
  }

  private determineNumberOfSteps() {
    const stepsRoutes = [
      INVESTMENT_GOAL_ROUTE,
      'investment-type',
      'suggest-portfolio',
      'consult-comment',
      'consult-suggest-check',
      WITHDRAWAL_AVAILABLE_CLIENTS_ROUTE,
      WITHDRAWAL_FORM_ROUTE,
      WITHDRAWAL_CHECK_ROUTE
    ];
    if (stepsRoutes.some(route => this.router.url.includes(route))) {
      this.suggestingService.stepsLimited
        .subscribe(
          (limited) => {
            if(limited === STEPPER_LIMITATIONS.STEPS_WITHDRAWAL) {
              return this.STEPS_WITHDRAWAL_FORM;
            } else if (limited === STEPPER_LIMITATIONS.STEPS_WITHDRAWAL_LIMITED) {
              return this.STEPS_WITHDRAWAL_FORM_LIMITED;
            } else if(limited === STEPPER_LIMITATIONS.STEPS_LIMITED) {
              return this.STEPS_LIMITED;
            } else {
              return this.STEPS_ALL;
            }
        });
    }
  }

  private adjustStepIfLimited(step: number): number {
    const stepsDiff = this.STEPS_ALL - this.STEPS_LIMITED;

    if (this.numOfSteps === this.STEPS_LIMITED && !this.consultClientService.getAllSteps().newClient){
      return step - stepsDiff;
    }
    return step;
  }

  public ngOnInit(): void {
    this.stepsSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      if(RESET_WITHDRAWAL_DRAFT_ROUTES.some(route => event.url.includes(route))) {
        this.withdrawalDraftStorageService.resetDraft();
      }
      this.determineClientDetailMode();
      this.determineNumberOfSteps();
    });
  }

  public ngOnDestroy(): void {
    this.stepsSub?.unsubscribe();
  }
}
